import React from 'react';
import PropTypes from 'prop-types';
import { TextField, FormControl, InputLabel, Select, MenuItem, Autocomplete, Chip, Box, Typography } from '@mui/material';
import { getFieldSchema as getSchemaField } from './tradeSchema';
import { useUsers } from '../../../../hooks/useUsers';
import { useScripts } from '../../../../hooks/useScripts';
import modifyScriptName from '../../../../utils/modifyScriptName';

export const getFieldSchema = (fieldName) => getSchemaField(fieldName) || {};

// Component for user selection input
const UserSelectInput = ({ condition, updateCondition, fieldSchema }) => {
  const { users, loading } = useUsers(fieldSchema.userRole);
  const isMultiSelect = ['in', 'nin'].includes(condition.operator);

  // Normalize condition value to array
  const normalizeValue = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') return value.split(',').map(v => v.trim());
    return [];
  };

  const normalizedValue = normalizeValue(condition.value);

  if (isMultiSelect) {
    return (
      <Autocomplete
        multiple
        size="small"
        options={users}
        getOptionLabel={(option) => `${option.firstname} ${option.lastname} (${option.username})`}
        value={users.filter(user => normalizedValue.includes(user._id)) || []}
        onChange={(event, newValue) => {
          const selectedIds = newValue.map(user => user._id);
          updateCondition(condition.id, 'value', selectedIds);
        }}
        loading={loading}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={`${option.firstname} ${option.lastname}`}
              {...getTagProps({ index })}
              key={option._id}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Users"
            placeholder={loading ? "Loading users..." : "Select multiple users"}
          />
        )}
        fullWidth
      />
    );
  }

  return (
    <Autocomplete
      size="small"
      options={users}
      getOptionLabel={(option) => `${option.firstname} ${option.lastname} (${option.username})`}
      value={users.find(user => user._id === condition.value) || null}
      onChange={(event, newValue) => {
        updateCondition(condition.id, 'value', newValue ? newValue._id : '');
      }}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select User"
          placeholder={loading ? "Loading users..." : "Select a user"}
        />
      )}
      fullWidth
    />
  );
};

UserSelectInput.propTypes = {
  condition: PropTypes.object.isRequired,
  updateCondition: PropTypes.func.isRequired,
  fieldSchema: PropTypes.object.isRequired,
};

// Helper function to format script display with expiry date and exchange
const formatScriptDisplay = (script) => {
  if (!script) return '';
  
  const symbol = script.tradingsymbol || '';
  const exchange = script.exchange?.name || '';
  
  // Format expiry date from YYYYMMDD to dd/mm/yyyy
  let formattedExpiry = '';
  if (script.expiry) {
    try {
      const expiryStr = script.expiry.toString();
      if (expiryStr.length === 8) {
        // Format YYYYMMDD to dd/mm/yyyy
        const year = expiryStr.substring(0, 4);
        const month = expiryStr.substring(4, 6);
        const day = expiryStr.substring(6, 8);
        formattedExpiry = `${day}/${month}/${year}`;
      } else {
        // If it's already a date string, parse and format it
        const date = new Date(script.expiry);
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        formattedExpiry = `${d}/${m}/${y}`;
      }
    } catch (error) {
      console.warn('Error formatting expiry date:', error);
      formattedExpiry = script.expiry;
    }
  }
  
  // Build display string
  let display = symbol;
  if (formattedExpiry) {
    display += ` (${formattedExpiry})`;
  }
  if (exchange) {
    display += ` (${exchange})`;
  }
  
  return display;
};

// Component for script selection input
const ScriptSelectInput = ({ condition, updateCondition, fieldSchema }) => {
  const { scripts, loading } = useScripts();
  const isMultiSelect = ['in', 'nin'].includes(condition.operator);

  // Normalize condition value to array
  const normalizeValue = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') return value.split(',').map(v => v.trim());
    return [];
  };

  const normalizedValue = normalizeValue(condition.value);

  if (isMultiSelect) {
    return (
      <Autocomplete
        multiple
        size="small"
        options={scripts}
        getOptionLabel={(option) => formatScriptDisplay(option)}
        value={scripts.filter(script => normalizedValue.includes(script._id)) || []}
        onChange={(event, newValue) => {
          const selectedIds = newValue.map(script => script._id);
          updateCondition(condition.id, 'value', selectedIds);
        }}
        loading={loading}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={formatScriptDisplay(option)}
              {...getTagProps({ index })}
              key={option._id}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Scripts"
            placeholder="Select scripts"
          />
        )}
        fullWidth
      />
    );
  }

  return (
    <Autocomplete
      size="small"
      options={scripts}
      getOptionLabel={(option) => formatScriptDisplay(option)}
      value={scripts.find(script => script._id === condition.value) || null}
      onChange={(event, newValue) => {
        updateCondition(condition.id, 'value', newValue ? newValue._id : '');
      }}
      loading={loading}
      isOptionEqualToValue={(option, value) => option._id === value._id}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Script"
          placeholder="Select a script"
        />
      )}
      fullWidth
    />
  );
};

ScriptSelectInput.propTypes = {
  condition: PropTypes.object.isRequired,
  updateCondition: PropTypes.func.isRequired,
  fieldSchema: PropTypes.object.isRequired,
};

// Helper function to get input type based on field schema type
const getInputType = (fieldType) => {
  if (fieldType === 'date') return 'date';
  if (fieldType === 'datetime') return 'datetime-local';
  return 'number';
};

// Helper function to validate and handle number inputs
const handleNumberInput = (value, fieldSchema, updateCondition, conditionId, fieldName) => {
  // Allow empty values
  if (value === '') {
    updateCondition(conditionId, fieldName, value);
    return;
  }

  // Validate number format
  const numValue = parseFloat(value);
  if (Number.isNaN(numValue)) {
    // Don't update if it's not a valid number
    return;
  }

  // Check min/max constraints if they exist
  if (fieldSchema.min !== undefined && numValue < fieldSchema.min) {
    return;
  }
  if (fieldSchema.max !== undefined && numValue > fieldSchema.max) {
    return;
  }

  updateCondition(conditionId, fieldName, value);
};

export function renderValueInput(condition, updateCondition) {
  const fieldSchema = getFieldSchema(condition.field);
  const isRangeOperator = condition.operator === 'range';

  if (!fieldSchema || !fieldSchema.type) {
    return (
      <TextField
        fullWidth
        size="small"
        label="Value"
        value={condition.value || ''}
        onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
        placeholder="Select a field first"
        disabled
      />
    );
  }
  if (fieldSchema.type === 'boolean') {
    return (
      <FormControl fullWidth size="small">
        <InputLabel>Value</InputLabel>
        <Select
          value={condition.value || ''}
          label="Value"
          onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
        >
          <MenuItem value="true">True</MenuItem>
          <MenuItem value="false">False</MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (fieldSchema.type === 'user-select') {
    return <UserSelectInput condition={condition} updateCondition={updateCondition} fieldSchema={fieldSchema} />;
  }

  if (fieldSchema.type === 'script-select') {
    return <ScriptSelectInput condition={condition} updateCondition={updateCondition} fieldSchema={fieldSchema} />;
  }

  if (fieldSchema.type === 'select' && fieldSchema.options) {
    if (condition.operator === 'in' || condition.operator === 'nin') {
      return (
        <Autocomplete
          multiple
          size="small"
          options={fieldSchema.options}
          getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
          value={condition.value ? 
            fieldSchema.options.filter(option => {
              const optionValue = typeof option === 'string' ? option : option.value;
              return Array.isArray(condition.value) 
                ? condition.value.includes(optionValue)
                : condition.value.split(',').map(v => v.trim()).includes(optionValue);
            }) : []
          }
          onChange={(event, newValue) => {
            const selectedValues = newValue.map(option => 
              typeof option === 'string' ? option : option.value
            );
            updateCondition(condition.id, 'value', selectedValues);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Values"
              placeholder="Choose multiple values"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip 
                variant="outlined" 
                label={typeof option === 'string' ? option : option.label} 
                {...getTagProps({ index })} 
                key={typeof option === 'string' ? option : option.value} 
              />
            ))
          }
        />
      );
    }
    return (
      <FormControl fullWidth size="small">
        <InputLabel>Value</InputLabel>
        <Select
          value={condition.value || ''}
          label="Value"
          onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
        >
          {fieldSchema.options.map(option => {
            const optionValue = typeof option === 'string' ? option : option.value;
            const optionLabel = typeof option === 'string' ? option : option.label;
            return (
              <MenuItem key={optionValue} value={optionValue}>{optionLabel}</MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  }

  if (fieldSchema.type === 'string' && condition.operator === 'in') {
    return (
      <TextField
        fullWidth
        size="small"
        label="Values (comma separated)"
        value={condition.value || ''}
        onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
        placeholder="value1, value2, value3"
        helperText="Enter multiple values separated by commas"
      />
    );
  }

  if (isRangeOperator) {
    return (
      <Box display="flex" gap={1} alignItems="center" width="100%">        <TextField
          size="small"
          label="From"
          type={getInputType(fieldSchema.type)}
          value={condition.value || ''}
          onChange={(e) => {
            if (fieldSchema.type === 'number') {
              handleNumberInput(e.target.value, fieldSchema, updateCondition, condition.id, 'value');
            } else {
              updateCondition(condition.id, 'value', e.target.value);
            }
          }}
          InputLabelProps={fieldSchema.type === 'date' || fieldSchema.type === 'datetime' ? { shrink: true } : {}}
          inputProps={fieldSchema.type === 'number' ? {
            min: fieldSchema.min,
            max: fieldSchema.max,
            step: fieldSchema.step || 'any'
          } : {}}
          sx={{ flex: 1 }}
        />
        <Typography variant="body2" color="text.secondary">to</Typography>
        <TextField
          size="small"
          label="To"
          type={getInputType(fieldSchema.type)}
          value={condition.valueEnd || ''}
          onChange={(e) => {
            if (fieldSchema.type === 'number') {
              handleNumberInput(e.target.value, fieldSchema, updateCondition, condition.id, 'valueEnd');
            } else {
              updateCondition(condition.id, 'valueEnd', e.target.value);
            }
          }}
          InputLabelProps={fieldSchema.type === 'date' || fieldSchema.type === 'datetime' ? { shrink: true } : {}}
          inputProps={fieldSchema.type === 'number' ? {
            min: fieldSchema.min,
            max: fieldSchema.max,
            step: fieldSchema.step || 'any'
          } : {}}
          sx={{ flex: 1 }}
        />
      </Box>
    );
  }

  if (fieldSchema.type === 'number') {
    return (
      <TextField
        fullWidth
        size="small"
        label="Value"
        type="number"
        value={condition.value || ''}
        onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
        placeholder={fieldSchema.placeholder}
        inputProps={{ step: 'any' }}
      />
    );
  }
  if (fieldSchema.type === 'date' || fieldSchema.type === 'datetime') {
    return (
      <TextField
        fullWidth
        size="small"
        label="Value"
        type={getInputType(fieldSchema.type)}
        value={condition.value || ''}
        onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    );
  }
  return (
    <TextField
      fullWidth
      size="small"
      label="Value"
      type={fieldSchema.type === 'number' ? 'number' : 'text'}
      value={condition.value || ''}
      onChange={(e) => {
        if (fieldSchema.type === 'number') {
          handleNumberInput(e.target.value, fieldSchema, updateCondition, condition.id, 'value');
        } else {
          updateCondition(condition.id, 'value', e.target.value);
        }
      }}
      placeholder={fieldSchema.placeholder}
      inputProps={fieldSchema.type === 'number' ? {
        min: fieldSchema.min,
        max: fieldSchema.max,
        step: fieldSchema.step || 'any'
      } : {}}
    />
  );
}
